import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const benefits = [
    {
      icon: "Zap",
      title: "Exclusive Deals",
      description: "Get early access to flash sales and member-only discounts up to 40% off"
    },
    {
      icon: "Bell",
      title: "New Arrivals",
      description: "Be the first to know about latest tech releases and product launches"
    },
    {
      icon: "Gift",
      title: "Special Offers",
      description: "Receive personalized recommendations and birthday surprises"
    },
    {
      icon: "TrendingUp",
      title: "Tech Insights",
      description: "Weekly tech news, buying guides, and expert product comparisons"
    }
  ];

  const handleSubscribe = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 2000);
  };

  if (isSubscribed) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="holographic-card p-12 rounded-3xl">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Icon name="Check" size={40} color="white" />
              </motion.div>
              
              <h3 className="text-3xl font-bold text-foreground mb-4">
                Welcome to the ElectroShop Pro Family!
              </h3>
              
              <p className="text-lg text-muted-foreground mb-6">
                You're now subscribed to our newsletter. Check your inbox for a special welcome offer!
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="Gift" size={16} className="text-accent" />
                  <span>Welcome discount sent</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} className="text-primary" />
                  <span>Weekly updates enabled</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-primary rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-accent rounded-full blur-2xl animate-pulse delay-2000"></div>
      </div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2">
                  <Icon name="Mail" size={16} className="text-accent" />
                  <span className="text-sm font-mono text-accent">STAY CONNECTED</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                  <span className="text-foreground">Join Our</span>
                  <br />
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Tech Community
                  </span>
                </h2>
                
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Get exclusive access to deals, new arrivals, and tech insights. 
                  Join over 50,000 tech enthusiasts who trust our recommendations.
                </p>
              </div>

              {/* Newsletter Form */}
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                onSubmit={handleSubscribe}
                className="space-y-4"
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      required
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    loading={isLoading}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="trust-seal whitespace-nowrap"
                  >
                    Subscribe Now
                  </Button>
                </div>
                
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Lock" size={14} />
                  <span>We respect your privacy. Unsubscribe at any time.</span>
                </div>
              </motion.form>

              {/* Special Offer */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 rounded-2xl p-6"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-accent/20 rounded-full">
                    <Icon name="Gift" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Welcome Bonus!</h3>
                    <p className="text-sm text-muted-foreground">
                      Get 15% off your first purchase when you subscribe today
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Content - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="holographic-card p-8 rounded-3xl">
                <h3 className="text-2xl font-bold text-foreground mb-8 text-center">
                  What You'll Get
                </h3>
                
                <div className="space-y-6">
                  {benefits?.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.1 * index }}
                      className="flex items-start space-x-4 group"
                    >
                      <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg group-hover:bg-primary/20 transition-colors">
                        <Icon name={benefit?.icon} size={20} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-2">
                          {benefit?.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {benefit?.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Social Proof */}
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={16} className="text-primary" />
                      <span>50,000+ subscribers</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Star" size={16} className="text-accent" />
                      <span>4.9/5 rating</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;